import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { Role } from '@/routes/routeConfig';
import type { SessionData } from '@/api/generated/authApi';

/**
 * The authenticated session, held in memory only.
 *
 * <h2>Why nothing here is persisted</h2>
 * Brief §9, restated as a Phase 1 decision: the access token lives in memory and never in
 * {@code localStorage}. A token in {@code localStorage} is readable by any XSS payload on the
 * page; a token in a closure/Redux is not exfiltrated by reading storage. The cost is that a
 * page reload loses it — which is exactly what the refresh cookie is for. On startup the app
 * calls {@code /auth/refresh} once (the cookie survives the reload), and either re-establishes
 * the session or resolves to anonymous. That round trip is the {@code status: 'unknown'} state
 * below, and it is why routes must wait for bootstrap rather than treating "no token yet" as
 * "logged out".
 */

export interface AuthUser {
  id: number;
  displayName: string;
  roles: Role[];
}

export type AuthStatus =
  // Bootstrap refresh has not resolved yet. Routes must not redirect to /login on this, or a
  // reload would bounce an authenticated user out before their session is restored.
  'unknown' | 'authenticated' | 'anonymous';

export interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  status: AuthStatus;
}

const initialState: AuthState = {
  accessToken: null,
  user: null,
  status: 'unknown',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** A verify-otp or refresh response landed: we are signed in. */
    sessionEstablished(state, action: PayloadAction<SessionData>) {
      const session = action.payload;
      state.accessToken = session.accessToken ?? null;
      state.user =
        session.userId != null
          ? {
              id: session.userId,
              displayName: session.displayName ?? '',
              roles: (session.roles ?? []) as Role[],
            }
          : null;
      state.status = 'authenticated';
    },

    /** The vendor renamed their business; the header shows the new name at once. */
    displayNameChanged(state, action: PayloadAction<string>) {
      if (state.user) {
        state.user.displayName = action.payload;
      }
    },

    /** Logout, a failed refresh, or a bootstrap that found no session. */
    sessionCleared(state) {
      state.accessToken = null;
      state.user = null;
      state.status = 'anonymous';
    },
  },
});

export const { sessionEstablished, displayNameChanged, sessionCleared } = authSlice.actions;
export const authReducer = authSlice.reducer;

// Selectors. Kept here so components never reach into the state shape directly.
interface HasAuth {
  auth: AuthState;
}

export const selectAccessToken = (state: HasAuth) => state.auth.accessToken;
export const selectAuthStatus = (state: HasAuth) => state.auth.status;
export const selectCurrentUser = (state: HasAuth) => state.auth.user;
export const selectIsAuthenticated = (state: HasAuth) => state.auth.status === 'authenticated';
